/**
 * ProductSwitcher — the sidebar's product identity + switcher (Stage A,
 * Curium-style translated to loremap tokens).
 *
 * Expanded rail: a bordered pill (radius-panel, token border) — the
 * product's mark (32px), its name (14px/600, ellipsized), and a chevron
 * that rotates 180° while the dropdown is open. The dropdown sits directly
 * below at the trigger's width: every product, in the order the Products
 * page's drag-to-reorder saved (features/products/order.ts, the one list
 * every product surface reads), then
 * "+ New product" (the shared create dialog) and "Manage products"
 * (/products). One product is still a menu (2026-09-05; owner: "When I only
 * have one product, the product picker isn't clickable. I can't add more
 * products"): New product lives in it.
 *
 * Collapsed rail: a mark-only button whose panel opens to the RIGHT.
 *
 * A tiny accent dot on the trigger means sessions in OTHER products are
 * writing or unread (sessionActivity) — the cross-product attention signal
 * that replaced the global writing chip.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';

import { useProduct, useProducts } from '../api/queries';
import type { ProductSummary, Ulid } from '../api/types';
// The dialog's own file, not the products barrel: the barrel carries the
// products pages, which load on their first visit (routes.tsx).
import { CreateProductDialog } from '../features/products/CreateProductDialog';
import { applyOrder, loadOrder, subscribeOrder } from '../features/products/order';
import { ProductMark } from '../features/products/ProductMark';
import { productPath } from '../lib/deepLink';
import { hasOtherProductActivity, useSessionActivity } from './sessionActivity';

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={`shrink-0 text-tertiary transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** One dropdown row; fetches its product detail for the avatar mark. */
function SwitcherItem({
  product,
  active,
  onPick,
}: {
  product: ProductSummary;
  active: boolean;
  onPick: (pid: Ulid) => void;
}) {
  const detail = useProduct(product.id);
  return (
    <button
      type="button"
      data-testid="switcher-item"
      data-product={product.id}
      data-active={active ? 'true' : undefined}
      onClick={() => onPick(product.id)}
      className={`flex w-full cursor-pointer items-center gap-2 rounded-card border-none px-2 py-1.5 text-left text-[length:var(--text-ui-size)] transition-colors duration-(--motion-hover) ${
        active
          ? 'bg-accent-subtle font-semibold text-primary'
          : 'bg-transparent text-secondary hover:bg-inset hover:text-primary'
      }`}
    >
      <ProductMark name={product.name} repos={detail.data?.repos ?? []} size={24} />
      <span className="min-w-0 flex-1 truncate">{product.name}</span>
    </button>
  );
}

function SwitcherPanel({
  products,
  activeProductId,
  onPick,
  onNewProduct,
  onManage,
  className,
}: {
  products: ProductSummary[];
  activeProductId: Ulid | null;
  onPick: (pid: Ulid) => void;
  onNewProduct: () => void;
  onManage: () => void;
  className: string;
}) {
  return (
    <div
      data-testid="switcher-panel"
      className={`z-50 rounded-panel border border-default bg-raised p-1 shadow-overlay ${className}`}
    >
      <div className="max-h-60 overflow-y-auto">
        {products.map((p) => (
          <SwitcherItem
            key={p.id}
            product={p}
            active={p.id === activeProductId}
            onPick={onPick}
          />
        ))}
      </div>
      <div className="mt-1 border-t border-default pt-1">
        <button
          type="button"
          data-testid="switcher-new-product"
          onClick={onNewProduct}
          className="flex w-full cursor-pointer items-center gap-2 rounded-card border-none bg-transparent px-2 py-1.5 text-left text-[length:var(--text-ui-size)] text-secondary transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary"
        >
          <span aria-hidden className="flex w-6 justify-center text-tertiary">
            +
          </span>
          New product
        </button>
        <button
          type="button"
          data-testid="switcher-manage"
          onClick={onManage}
          className="flex w-full cursor-pointer items-center gap-2 rounded-card border-none bg-transparent px-2 py-1.5 text-left text-[length:var(--text-ui-size)] text-secondary transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary"
        >
          <span aria-hidden className="w-6" />
          Manage products
        </button>
      </div>
    </div>
  );
}

export function ProductSwitcher({
  activeProductId,
  railCollapsed,
}: {
  activeProductId: Ulid | null;
  railCollapsed: boolean;
}) {
  const navigate = useNavigate();
  const products = useProducts();
  const active = useProduct(activeProductId ?? undefined);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const otherActivity = useSessionActivity((s) =>
    hasOtherProductActivity(s, activeProductId),
  );

  // The reader's order, the one the Products page saves: re-sorted the
  // moment a row drops there (order.ts broadcasts it).
  const order = useSyncExternalStore(subscribeOrder, loadOrder);
  const list = applyOrder(products.data?.products ?? [], order);
  const activeName =
    active.data?.name ??
    list.find((p) => p.id === activeProductId)?.name ??
    'Product';

  // Outside-mousedown + Escape close.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (activeProductId === null) return null;

  const pick = (pid: Ulid) => {
    setOpen(false);
    if (pid !== activeProductId) void navigate(productPath(pid));
  };
  const manage = () => {
    setOpen(false);
    void navigate('/products');
  };
  const openCreate = () => {
    setOpen(false);
    setCreating(true);
  };

  const dot = otherActivity ? (
    <span
      data-testid="switcher-activity-dot"
      aria-hidden
      className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-pill bg-(--accent)"
    />
  ) : null;

  const panel = (positionClass: string) => (
    <SwitcherPanel
      products={list}
      activeProductId={activeProductId}
      onPick={pick}
      onNewProduct={openCreate}
      onManage={manage}
      className={positionClass}
    />
  );

  return (
    <div ref={rootRef} className="relative">
      {railCollapsed ? (
        <>
          <button
            type="button"
            data-testid="product-switcher"
            title={activeName}
            aria-label={`Switch product (${activeName})`}
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={() => setOpen((o) => !o)}
            className="relative flex w-full cursor-pointer items-center justify-center rounded-panel border border-default bg-transparent py-2 hover:border-strong"
          >
            <ProductMark name={activeName} repos={active.data?.repos ?? []} size={28} />
            {dot}
          </button>
          {open ? panel('absolute top-0 left-full ml-2 w-56') : null}
        </>
      ) : (
        <>
          <button
            type="button"
            data-testid="product-switcher"
            title={activeName}
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={() => setOpen((o) => !o)}
            className="relative flex h-[50px] w-full cursor-pointer items-center gap-2 rounded-panel border border-default bg-transparent px-2.5 text-left hover:border-strong"
          >
            <ProductMark name={activeName} repos={active.data?.repos ?? []} size={32} />
            <span className="min-w-0 flex-1 truncate text-[length:var(--text-ui-size)] font-semibold text-primary">
              {activeName}
            </span>
            <Chevron open={open} />
            {dot}
          </button>
          {open ? panel('absolute inset-x-0 top-[calc(100%+4px)]') : null}
        </>
      )}
      <CreateProductDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(detail) => void navigate(productPath(detail.id), { state: { connect: true } })}
      />
    </div>
  );
}
